import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import { FieldConfig, FormConfig } from '../../components/Fields/Types';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import UserEditModel from '../../models/UserEditModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult, Result } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import UserControl from './Components/UserControl';
import User_popup_ChangePassword from './Popups/User_popup_ChangePassword';
import User_popup_ChangeProfilePicture from './Popups/User_popup_ChangeProfilePicture';
import User_popup_GenerateAttachment from './Popups/User_popup_GenerateAttachment';
import User_popup_OutOfOffice from './Popups/User_popup_OutOfOffice';
import User_tab_ApplicationRoles from './Tabs/User_tab_ApplicationRoles';
import User_tab_Attachments from './Tabs/User_tab_Attachments';
import User_tab_Following from './Tabs/User_tab_Following';
import User_tab_Projects from './Tabs/User_tab_Projects';

const UserDetailsView: React.FC = () => {
    const { user, changeProfilePicture } = useAuth();

    let userId = user?.userId;
    let idParams: IdParams = useParams();

    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        userId = parseInt(idParams.id);
    }

    const [serverErrors, setServerErrors] = useState<DataResult<UserEditModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [formData, setFormData] = useState<UserEditModel>();
    const [attachmentRefreshCounter, setAttachmentRefreshCounter] = useState<number>(0)
    const [businessPartnersRefreshCounter, setBusinessPartnersRefreshCounter] = useState<number>(0)
    const [itemsRefreshCounter, setItemsRefreshCounter] = useState<number>(0)
    const [showChangeProfilePicturePopup, setShowChangeProfilePicturePopup] = useState<boolean>(false);
    const [showGenerateDocumentPopup, setShowGenerateDocumentPopup] = useState<boolean>(false);
    const [showOutOfOfficePopup, setShowOutOfOfficePopup] = useState<boolean>(false);
    const [showChangePasswordPopup, setShowChangePasswordPopup] = useState<boolean>(false);
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const navigate = useNavigate()

    useEffect(() => {
        if (userId !== undefined) {
            DataService.getById<UserEditModel>('api/v1/Users/', userId)
                .then(response => {
                    setFormData(response);
                })
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [userId]);

    function submit() {
        if (formData) {
            DataService
                .update<UserEditModel>(`api/v1/Users/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    function setFieldValue(name: string, value: string) {
        if (formData) {
            name.split('.').reduce((object, propertyName, index, elements) => {
                if (index === elements.length - 1) {
                    object[propertyName] = value;
                    setFormData(formData);
                }

                if (object && object[propertyName]) {
                    return object[propertyName];
                }
                return null;
            }, formData);
        }
    }

    function resetPassword() {
        if (formData)
            DataService.post<Result>('api/v1/Users/' + formData.id + '/resetPassword', {})
                .then(response => {
                    handleResult(response);
                });
    }

    function disableUser() {
        if (formData)
            DataService.post<Result>('api/v1/Users/' + formData.id + '/disable', {})
                .then(response => {
                    handleResult(response);
                    if (response.isSuccesful === true) {
                        formData.isEnabled = false;
                        setFormData({ ...formData });
                    }
                });
    }

    function enableUser() {
        if (formData)
            DataService.post<Result>('api/v1/Users/' + formData.id + '/enable', {})
                .then(response => {
                    handleResult(response);
                    if (response.isSuccesful === true) {
                        formData.isEnabled = true;
                        formData.isLocked = false;
                        setFormData({ ...formData });
                    }
                });
    }

    function onProfilePictureChanged(thumbnailUrl?: string) {
        if (formData && thumbnailUrl) {
            formData.fileUrl = thumbnailUrl;
            setFormData({ ...formData });
            changeProfilePicture(thumbnailUrl);
            setAttachmentRefreshCounter(attachmentRefreshCounter + 1);
        }
    }


    let fields: FieldConfig[] = [
        { name: 'name', label: 'Name', labelSize: 4, validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 250 }] },
        { name: 'loginName', label: 'Login name', labelSize: 4, validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 250 }] },
        { name: 'language', label: 'Language', labelSize: 4 },
        { name: 'email', label: 'Email', labelSize: 4, validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 250 }] },
        { name: 'isEnabled', label: 'Enabled', labelSize: 4 },
        { name: 'isLocked', label: 'Locked', labelSize: 4 },
        { name: 'employee.firstName', label: 'Name', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 250 }] },
        { name: 'employee.prePosition', label: 'Name', labelSize: 3, validationRules: [{ type: 'isMaxLength', maxLength: 250 }] },
        { name: 'employee.lastName', label: 'Name', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 250 }] },
        { name: 'employee.genderId', label: 'Gender', labelSize: 3 },
        { name: 'employee.departmentId', label: 'Department', labelSize: 3 },
        { name: 'businessPartnerUser.businessPartner.businessPartnerTypeId', label: 'Type', labelSize: 3 },
        { name: 'businessPartnerUser.businessPartner.name', label: 'Name', labelSize: 3 },
        { name: 'businessPartnerUser.contactperson', label: 'Contactperson', labelSize: 3 },
    ];

    const formConfig: FormConfig = {
        fields: fields,
        handleSubmit: submit,
        handleFieldChanged: setFieldValue,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);
    const { isAuthorizedFor } = useAuth();


    if (userId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.CreateUser)) {
            return (<Unauthorized />);
        }
    }
    else if (userId !== user?.userId && !isAuthorized) {
        return (<Unauthorized />);
    }

    if (!formData)
        return <SpinnerOverlay />

    const formActions =
        <ActionMenu title="Actions">
            {userId === user?.userId && <ActionMenuItem width={250} icon="fa fa-id-badge" title="Set profile picture" appFunction={ApplicationFunctions.ChangeProfilePicture} onClick={() => { setShowChangeProfilePicturePopup(true); }} />}
            <ActionMenuItem icon="fa fa-magic" title="Generate a document" appFunction={ApplicationFunctions.CreateAttachment} onClick={() => { console.log('selected'); setShowGenerateDocumentPopup(true); }} />
            <ActionMenuItem icon="fa fa-sign-out" title="Out of Office" appFunction={ApplicationFunctions.SetOutOfOffice} onClick={() => { setShowOutOfOfficePopup(true); }} />
            <ActionMenuItem icon="fa fa-edit" title="Reset password and unlock" appFunction={ApplicationFunctions.ResetPassword} onClick={() => { resetPassword(); }} />
            {userId === user?.userId && <ActionMenuItem icon="fa fa-edit" title="Change password" onClick={() => { setShowChangePasswordPopup(true); }} />}
            {userId !== user?.userId && formData.isEnabled === true && <ActionMenuItem icon="fa fa-ban" iconVariant="danger" title="Disable user" appFunction={ApplicationFunctions.EnableDisableUser} onClick={disableUser} />}
            {userId !== user?.userId && formData.isEnabled === false && <ActionMenuItem icon="fa fa-check" iconVariant="success" title="Enable user" appFunction={ApplicationFunctions.EnableDisableUser} onClick={enableUser} />}
            {formData.employee && (<ActionMenuItem icon="fa fa-user" title="Goto employee" appFunction={ApplicationFunctions.ViewEmployee} onClick={() => { navigate('/plm/UserManagement/Employee/' + formData.employee.id); }} />)}
        </ActionMenu>

    return (
        <div className='main-content'>
            <Form
                title={formData.name}
                actions={formActions}
                footer={(
                    <Row>
                        <Col md='3' className='hidden-md'>
                            <ButtonBar offset={0} >
                            </ButtonBar>
                        </Col>
                        <Col md='5'>
                            <ButtonBar offset={4}>
                                <Authorize applicationFunction={ApplicationFunctions.UpdateUser} >
                                    <SaveButton  {...getSubmitButtonProps()} />
                                </Authorize>
                            </ButtonBar>
                        </Col>
                        <Col md='4' className='hidden-md'>
                            <ButtonBar offset={0} >
                            </ButtonBar>
                        </Col>
                    </Row>
                )}>
                <UserControl formData={formData} getLabelProps={getLabelProps} getControlProps={getControlProps} getFieldProps={getFieldProps} />
            </Form>
            {userId && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='main-tabs'
                                defaultActiveKey={'tab_applicationroles'}
                                tabs={[
                                    {
                                        title: 'Application roles',
                                        eventKey: 'tab_applicationroles',
                                        component: <User_tab_ApplicationRoles userId={userId} />
                                    },
                                    {
                                        title: 'Attachments',
                                        eventKey: 'tab_attachments',
                                        component: <User_tab_Attachments userId={userId} />
                                    },
                                    {
                                        title: 'Projects',
                                        eventKey: 'tab_projects',
                                        component: <User_tab_Projects userId={userId} />
                                    },
                                    {
                                        title: 'Following items',
                                        eventKey: 'tab_following',
                                        component: <User_tab_Following userId={userId} />,
                                    }
                                ]}
                            />
                        </Col>
                    </Row>
                    {showChangeProfilePicturePopup && <User_popup_ChangeProfilePicture userId={userId} show={showChangeProfilePicturePopup} onHide={(thumbnailUrl?: string) => { setShowChangeProfilePicturePopup(false); onProfilePictureChanged(thumbnailUrl); }} />}
                    {showChangePasswordPopup && <User_popup_ChangePassword userId={userId} username={formData.loginName} show={showChangePasswordPopup} onHide={() => { setShowChangePasswordPopup(false); }} />}
                    {showGenerateDocumentPopup && <User_popup_GenerateAttachment userId={userId} show={showGenerateDocumentPopup} onHide={() => { setShowGenerateDocumentPopup(false); setBusinessPartnersRefreshCounter(businessPartnersRefreshCounter + 1) }} />}
                    {showOutOfOfficePopup && <User_popup_OutOfOffice userId={userId} show={showOutOfOfficePopup} onHide={() => { setShowOutOfOfficePopup(false); setItemsRefreshCounter(itemsRefreshCounter + 1) }} />}
                </Container>
            )}
        </div>
    );
}
export default UserDetailsView;